const createError = (message, config, response = null) => {
  const error = new Error(message);
  error.config = config;
  if (response) {
    error.response = response;
  }
  error.isFetchError = true;
  return error;
};

const parseResponseBody = async (response) => {
  if (response.status === 204 || response.status === 205) {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch (error) {
      console.warn('JSON Parse error.', {
        status: response.status,
        url: response.url,
        error,
      });
      return text;
    }
  }

  return text;
};

const performRequest = async (defaultConfig, config) => {
  const mergedConfig = {
    ...defaultConfig,
    ...config,
    headers: {
      ...defaultConfig.headers,
      ...config.headers,
    },
  };

  const baseURL = mergedConfig.baseURL || '';
  const requestURL = mergedConfig.url || '';

  const fullUrl =
    baseURL.endsWith('/') || requestURL.startsWith('/')
      ? baseURL.endsWith('/') && requestURL.startsWith('/')
        ? `${baseURL}${requestURL.substring(1)}`
        : `${baseURL}${requestURL}`
      : `${baseURL}/${requestURL}`;

  const fetchOptions = {
    method: mergedConfig.method.toUpperCase(),
    headers: mergedConfig.headers,
    ...mergedConfig.fetchOptions,
  };

  if (mergedConfig.data) {
    if (mergedConfig.data instanceof FormData) {
      fetchOptions.body = mergedConfig.data;
      delete fetchOptions.headers['Content-Type'];
    } else {
      fetchOptions.body = JSON.stringify(mergedConfig.data);
    }
  }

  try {
    const response = await fetch(fullUrl, fetchOptions);

    const responseHeaders = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    const responseData = await parseResponseBody(response);

    const customResponse = {
      data: responseData,
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      config: mergedConfig,
    };

    if (!response.ok) {
      throw createError(
        `Request failed with status code ${response.status}`,
        mergedConfig,
        customResponse,
      );
    }

    return customResponse;
  } catch (error) {
    if (error.isFetchError) {
      throw error;
    }
    throw createError(error.message, mergedConfig);
  }
};

const createInstance = (defaultConfig = {}) => {
  const instance = {};

  ['get', 'delete', 'head', 'options'].forEach((method) => {
    instance[method] = (url, config = {}) => {
      return performRequest(defaultConfig, { ...config, method, url });
    };
  });

  ['post', 'put', 'patch'].forEach((method) => {
    instance[method] = (url, data, config = {}) => {
      return performRequest(defaultConfig, { ...config, method, url, data });
    };
  });

  return instance;
};

const instance = (url, headers) => {
  const config = {
    baseURL: url || '/api',
    headers: headers || {
      'Content-Type': 'application/json',
    },
  };
  return createInstance(config);
};

export default instance;
